import { debounce, DebouncedFunc } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A hook that debounces a value with a specified delay.
 * @param value The value to debounce
 * @param delay The delay in milliseconds (default: 300ms)
 * @returns The debounced value
 */
export const useDebounce = <T>(value: T, delay: number = 300): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  const debouncedSetValue = useCallback(
    debounce((newValue: T) => {
      setDebouncedValue(newValue);
    }, delay),
    [delay]
  );

  useEffect(() => {
    debouncedSetValue(value);
    return debouncedSetValue.cancel;
  }, [value, debouncedSetValue]);

  return debouncedValue;
};

/**
 * A hook that creates a debounced version of a callback function.
 * @param callback The callback function to debounce
 * @param delay The delay in milliseconds (default: 300ms)
 * @returns A debounced version of the callback
 */
export const useDebouncedCallback = <T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300
) => {
  const debouncedFn = useRef<DebouncedFunc<T>>(null);

  useEffect(() => {
    debouncedFn.current = debounce(callback, delay);
    return () => {
      debouncedFn.current?.cancel();
    };
  }, [callback, delay]);

  return useCallback(
    (...args: Parameters<T>) => {
      debouncedFn.current?.(...args);
    },
    [debouncedFn]
  );
};
